/**
 * Singleton store for the navigation progress bar. Imperative API:
 *
 *   nprogress.start();   // 0% → trickling up
 *   nprogress.set(0.4);  // jump to 40%
 *   nprogress.inc(0.1);  // +10%
 *   nprogress.complete();// → 100% then fade out
 *   nprogress.reset();   // back to 0% (no animation)
 */

interface NProgressState {
  /** Progress 0..1, or `null` when idle/finished. */
  value: number | null;
  /** Running counter: `start` increments, `complete` decrements. */
  refCount: number;
}

type Listener = (state: NProgressState) => void;

let state: NProgressState = { value: null, refCount: 0 };
const listeners = new Set<Listener>();

function emit() { for (const l of listeners) l(state); }
function set(next: Partial<NProgressState>) { state = { ...state, ...next }; emit(); }

let trickleHandle: ReturnType<typeof setInterval> | null = null;
function startTrickle() {
  stopTrickle();
  trickleHandle = setInterval(() => {
    if (state.value === null) return;
    if (state.value >= 0.95) return;
    // Each tick, advance a small amount that slows as we approach 0.9.
    const remaining = 0.95 - state.value;
    set({ value: Math.min(0.95, state.value + remaining * 0.08) });
  }, 220);
}
function stopTrickle() {
  if (trickleHandle) { clearInterval(trickleHandle); trickleHandle = null; }
}

export const nprogress = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getState(): NProgressState {
    return state;
  },
  /** Begin a progress run. Multiple `start()` calls stack via refCount. */
  start(): void {
    const refCount = state.refCount + 1;
    if (state.value === null) {
      set({ value: 0.08, refCount });
      startTrickle();
    } else {
      set({ refCount });
    }
  },
  set(value: number): void {
    set({ value: clamp01(value) });
  },
  inc(amount = 0.1): void {
    if (state.value === null) return;
    set({ value: clamp01(state.value + amount) });
  },
  /** Mark one operation as complete. The bar only finishes when refCount hits 0. */
  complete(): void {
    const refCount = Math.max(0, state.refCount - 1);
    if (refCount > 0) { set({ refCount }); return; }
    stopTrickle();
    set({ value: 1, refCount: 0 });
    setTimeout(() => {
      if (state.value === 1 && state.refCount === 0) set({ value: null });
    }, 320);
  },
  /** Force the bar to 100% and dismiss (cancels any pending starts). */
  done(): void {
    stopTrickle();
    set({ value: 1, refCount: 0 });
    setTimeout(() => set({ value: null }), 320);
  },
  reset(): void {
    stopTrickle();
    set({ value: null, refCount: 0 });
  }
};

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
